import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createCarServiceApi,
  getCarServiceByIdApi,
  updateCarServiceByIdApi,
} from "../../api/car-service";
import { ICarService, ICarServiceRequest } from "../../interface/car-service";
import { setAppLoading } from "../../redux/app.reducer";
import { useAppDispatch } from "../../redux/hooks";
import useSnackbar from "../../utils/snackbar";
import { currencySymbol } from "../../utils/money";
import { getSrcImage } from "../../utils/utils";
import {
  IBandDraft,
  IBandErrors,
  IFareInfoDraft,
  ILegalCaps,
  ITierError,
  bandsToDraft,
  buildFareBands,
  buildFareInfo,
  deriveBandFroms,
  fareInfoToDraft,
  hasBandErrors,
  hasTierErrors,
  legalCapHint,
  validateBandDraft,
  validateFareInfoDraft,
} from "./fare";

interface ICarServiceForm {
  mode: "new" | "edit";
}
const CarServiceForm: FC<ICarServiceForm> = ({ mode }) => {
  let { id } = useParams();
  let navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar({ position: "bottom-left" });

  const [loading, setLoading] = useState(false);

  const [carService, setCarService] = useState<ICarServiceRequest>({
    name: "",
    picture: "",
    topViewPicture: "",
    detail: "",
    status: 1,
    areaDistance: 0,
    serviceType: 0,
    startPrice: 0,
    chargePerKilometer: 0,
    commissionPct: 0,
    chargePerTraffic: 0,
    callPrice: 0,
    insuranceFee: 0,
    fareInfo: null,
    baseDistanceKm: 1,
    fareBands: [],
    extraStopPrice: 0,
    extraStopCommissionPct: 0,
    multiStopEnabled: false,
  });

  const [bandDraft, setBandDraft] = useState<IBandDraft>({
    baseDistanceKm: "1",
    startPrice: "0",
    rows: [],
    openEndedRate: "",
  });
  const [bandErrors, setBandErrors] = useState<IBandErrors | null>(null);
  const [legalCaps, setLegalCaps] = useState<ILegalCaps | null>(null);

  const [fareInfoDraft, setFareInfoDraft] = useState<IFareInfoDraft>(
    fareInfoToDraft(null),
  );
  const [tierErrors, setTierErrors] = useState<ITierError[] | null>(null);

  // แก้ bandDraft แล้ว re-validate ทันทีถ้าเคย save fail มาก่อน (ไม่ validate ค่าเก่าที่ค้างใน closure)
  const updateBandDraft = (next: IBandDraft) => {
    setBandDraft(next);
    if (bandErrors) setBandErrors(validateBandDraft(next, legalCaps));
  };

  // แก้ fareInfoDraft แล้ว re-validate ทันทีถ้าเคย save fail มาก่อน (ไม่ validate ค่าเก่าที่ค้างใน closure)
  const updateFareInfoDraft = (next: IFareInfoDraft) => {
    setFareInfoDraft(next);
    if (tierErrors) setTierErrors(validateFareInfoDraft(next));
  };

  // ข้อความ error แรกที่พบ — ใช้ขึ้น snackbar
  const firstBandError = (e: IBandErrors): string | undefined => {
    if (e.baseDistanceKm) return e.baseDistanceKm;
    if (e.startPrice) return e.startPrice;
    for (const row of e.rows) {
      if (row.toKm) return row.toKm;
      if (row.ratePerKm) return row.ratePerKm;
    }
    return e.openEndedRate;
  };

  // ข้อความ error แรกที่พบใน fareInfo — ใช้ขึ้น snackbar
  const firstTierError = (errors: ITierError[]): string | undefined => {
    for (const err of errors) {
      if (err.fromKm) return err.fromKm;
      if (err.toKm) return err.toKm;
      if (err.pricePerKm) return err.pricePerKm;
    }
    return undefined;
  };

  const save = async () => {
    const errors =
      carService.serviceType === 0
        ? validateBandDraft(bandDraft, legalCaps)
        : null;
    if (errors && hasBandErrors(errors)) {
      setBandErrors(errors);
      openSnackbar(firstBandError(errors) || "Error: Something wrong.");
      return;
    }

    const tErr = validateFareInfoDraft(fareInfoDraft);
    if (hasTierErrors(tErr)) {
      setTierErrors(tErr);
      openSnackbar(firstTierError(tErr) || "Error: Something wrong.");
      return;
    }

    const payload: ICarServiceRequest = {
      ...carService, // echo ทุก field จาก GET (กัน full-replace ลบ field อนาคต)
      fareInfo: buildFareInfo(fareInfoDraft),
      ...(carService.serviceType === 0
        ? {
            startPrice: Number(bandDraft.startPrice),
            baseDistanceKm: Number(bandDraft.baseDistanceKm),
            fareBands: buildFareBands(bandDraft),
          }
        : {
            // BE zero-fills เมื่อ serviceType != 0 — ส่งค่า neutral กัน NaN จาก draft ที่ไม่ validate
            startPrice: 0,
            baseDistanceKm: 0,
            fareBands: [],
            extraStopPrice: 0,
            extraStopCommissionPct: 0,
            multiStopEnabled: false,
          }),
    };

    try {
      setLoading(true);

      if (mode === "edit") {
        await updateCarServiceByIdApi(id!, payload);
      } else {
        await createCarServiceApi(payload);
      }

      setLoading(false);
      openSnackbar("Successfuly");
      setTimeout(() => {
        navigate(`${process.env.PUBLIC_URL}/car-service`);
      }, 500);
    } catch (e: any) {
      setLoading(false);
      let msg: string | undefined;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something wrong.");
    }
  };

  const getCarServiceById = async () => {
    try {
      setLoading(true);

      const { data } = await getCarServiceByIdApi(id!);
      data as ICarService;

      setCarService(data);
      setBandDraft(bandsToDraft(data));
      setFareInfoDraft(fareInfoToDraft(data.fareInfo));
      setLegalCaps({
        baseFareLegalMin: data.baseFareLegalMin ?? null,
        baseFareLegalMax: data.baseFareLegalMax ?? null,
        perKmLegalMin: data.perKmLegalMin ?? null,
        perKmLegalMax: data.perKmLegalMax ?? null,
      });

      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      let msg: string | undefined;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something wrong.");
      navigate(`${process.env.PUBLIC_URL}/car-service`);
    }
  };

  useEffect(() => {
    if (mode === "edit") {
      getCarServiceById();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(setAppLoading(loading));
    // eslint-disable-next-line
  }, [loading]);
  return (
    <>
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  save();
                }}
              >
                <div className="row mb-2">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header">
                        <h4>เพิ่มบริการรถ</h4>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-12">
                            <div className="form-group row">
                              <label
                                htmlFor=""
                                className="col-sm-3 col-form-label"
                              >
                                ชื่อ
                              </label>
                              <div className="col-sm-3">
                                <input
                                  type="text"
                                  required
                                  className="form-control"
                                  placeholder="มอเตอร์ไซต์"
                                  value={carService.name}
                                  onChange={(e) => {
                                    setCarService((prev) => ({
                                      ...prev,
                                      name: e.target.value,
                                    }));
                                  }}
                                />
                              </div>
                            </div>

                            <div className="form-group row">
                              <label
                                htmlFor=""
                                className="col-sm-3 col-form-label"
                              >
                                รูปแบบการบริการ
                              </label>
                              <div className="col-sm-3">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={carService.detail}
                                  onChange={(e) => {
                                    setCarService((prev) => ({
                                      ...prev,
                                      detail: e.target.value,
                                    }));
                                  }}
                                />
                              </div>
                            </div>

                            <div className="form-group row">
                              <label
                                htmlFor=""
                                className="col-sm-3 col-form-label"
                              >
                                รูปภาพ
                              </label>
                              <div className="col-sm-3">
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="form-control"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      const reader = new FileReader();
                                      reader.onloadend = () => {
                                        const base64String =
                                          reader.result as string;
                                        setCarService((prev) => ({
                                          ...prev,
                                          picture: base64String.split(",")[1], // Remove data:image/...;base64, prefix
                                        }));
                                      };
                                      reader.readAsDataURL(file);
                                    }
                                  }}
                                />
                                {carService.picture && (
                                  <img
                                    src={getSrcImage(carService.picture)}
                                    alt="Preview"
                                    style={{
                                      maxWidth: "200px",
                                      marginTop: "10px",
                                    }}
                                  />
                                )}
                              </div>
                            </div>

                            <div className="form-group row">
                              <label
                                htmlFor=""
                                className="col-sm-3 col-form-label"
                              >
                                รูปภาพมุมบน (สำหรับแสดงบนแผนที่)
                              </label>
                              <div className="col-sm-3">
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="form-control"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      const reader = new FileReader();
                                      reader.onloadend = () => {
                                        const base64String =
                                          reader.result as string;
                                        setCarService((prev) => ({
                                          ...prev,
                                          topViewPicture:
                                            base64String.split(",")[1], // Remove data:image/...;base64, prefix
                                        }));
                                      };
                                      reader.readAsDataURL(file);
                                    }
                                  }}
                                />
                                {carService.topViewPicture && (
                                  <img
                                    src={getSrcImage(carService.topViewPicture)}
                                    alt="Preview"
                                    style={{
                                      maxWidth: "200px",
                                      marginTop: "10px",
                                    }}
                                  />
                                )}
                              </div>
                            </div>

                            <div className="form-group row">
                              <div className="col-lg-3">
                                <label htmlFor="name">สถานะ</label>
                              </div>
                              <div className="col-lg-3">
                                <select
                                  className="form-control"
                                  value={carService.status}
                                  onChange={(e) => {
                                    setCarService((prev) => ({
                                      ...prev,
                                      status: +e.target.value,
                                    }));
                                  }}
                                >
                                  <option value="1">ใช้งาน</option>
                                  <option value="0">ระงับ</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row mb-4 mt-4">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header">
                        <h4>ระยะการให้บริการ</h4>
                      </div>
                      <div className="card-body">
                        <div className="form-group row">
                          <div className="col-sm-3 col-form-label">
                            ระยะทางรับงานครอบคลุม(km)
                          </div>
                          <div className="col-sm-4">
                            <input
                              type="number"
                              step={1}
                              min={0}
                              className="form-control"
                              style={{ width: 80 }}
                              value={carService.areaDistance}
                              onChange={(e) => {
                                setCarService((prev) => ({
                                  ...prev,
                                  areaDistance: +e.target.value,
                                }));
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header">
                        <h4>ค่าบริการ</h4>
                      </div>
                      <div className="card-body">
                        <div className="form-group row">
                          <div className="col-sm-3">รูปแบบการคิดว่าบริการ</div>
                          <div className="col-sm-4">
                            <div className="custom-control custom-radio">
                              <input
                                type="radio"
                                id="serviceType0"
                                name="serviceType"
                                className="custom-control-input"
                                value={0}
                                checked={carService.serviceType === 0}
                                onChange={(e) => {
                                  setCarService((prev) => ({
                                    ...prev,
                                    serviceType: +e.target.value,
                                  }));
                                }}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="serviceType0"
                              >
                                คิดค่าบริการตามระบบ
                              </label>
                            </div>
                            <div className="custom-control custom-radio">
                              <input
                                type="radio"
                                id="serviceType1"
                                name="serviceType"
                                className="custom-control-input"
                                value={1}
                                checked={carService.serviceType === 1}
                                onChange={(e) => {
                                  setCarService((prev) => ({
                                    ...prev,
                                    serviceType: +e.target.value,
                                  }));
                                }}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="serviceType1"
                              >
                                คิดค่าบริการตามจริง/มิเตอร์/แล้วแต่ตกลงกับผู้ให้บริการ
                              </label>
                            </div>
                          </div>
                        </div>

                        {carService.serviceType === 1 ? (
                          <div className="form-group row">
                            <div className="col-3">
                              <span className="">
                                ค่าเรียกรถ ({currencySymbol})
                              </span>
                            </div>
                            <div className="col-sm-4">
                              <input
                                type="number"
                                className="form-control"
                                min={0}
                                step={1}
                                style={{ width: 80 }}
                                value={carService.callPrice}
                                onChange={(e) => {
                                  setCarService((prev) => ({
                                    ...prev,
                                    callPrice: +e.target.value,
                                  }));
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <>
                            <h6 className="mt-3">ขั้นบันไดค่าบริการ</h6>

                            <div className="form-group row">
                              <div className="col-sm-3">ระยะฐาน</div>
                              <div className="col-sm-9">
                                <div className="d-flex align-items-center flex-wrap">
                                  <input
                                    type="number"
                                    step={0.1}
                                    min={0.1}
                                    className={`form-control mr-2 ${
                                      bandErrors?.baseDistanceKm
                                        ? "is-invalid"
                                        : ""
                                    }`}
                                    style={{ width: 90 }}
                                    value={bandDraft.baseDistanceKm}
                                    onChange={(e) => {
                                      updateBandDraft({
                                        ...bandDraft,
                                        baseDistanceKm: e.target.value,
                                      });
                                    }}
                                  />
                                  <span className="mr-3">กม.แรก</span>
                                  <input
                                    type="number"
                                    step={1}
                                    min={0}
                                    className={`form-control mr-2 ${
                                      bandErrors?.startPrice ? "is-invalid" : ""
                                    }`}
                                    style={{ width: 90 }}
                                    value={bandDraft.startPrice}
                                    onChange={(e) => {
                                      updateBandDraft({
                                        ...bandDraft,
                                        startPrice: e.target.value,
                                      });
                                    }}
                                  />
                                  <span>บาท</span>
                                </div>
                                {bandErrors?.baseDistanceKm && (
                                  <div className="invalid-feedback d-block">
                                    {bandErrors.baseDistanceKm}
                                  </div>
                                )}
                                {bandErrors?.startPrice && (
                                  <div className="invalid-feedback d-block">
                                    {bandErrors.startPrice}
                                  </div>
                                )}
                                {legalCapHint(
                                  legalCaps?.baseFareLegalMin,
                                  legalCaps?.baseFareLegalMax,
                                  "บาท",
                                ) && (
                                  <small className="form-text text-muted d-block">
                                    {legalCapHint(
                                      legalCaps?.baseFareLegalMin,
                                      legalCaps?.baseFareLegalMax,
                                      "บาท",
                                    )}
                                  </small>
                                )}
                              </div>
                            </div>

                            {bandDraft.rows.map((row, i) => {
                              const froms = deriveBandFroms(bandDraft);
                              const rowErr = bandErrors?.rows[i];
                              const rateHint = legalCapHint(
                                legalCaps?.perKmLegalMin,
                                legalCaps?.perKmLegalMax,
                                "บาท/กม.",
                              );
                              return (
                                <div className="form-group row" key={i}>
                                  <div className="col-sm-3"></div>
                                  <div className="col-sm-9">
                                    <div className="d-flex align-items-center flex-wrap">
                                      <span className="mr-2">จาก</span>
                                      <input
                                        type="text"
                                        readOnly
                                        disabled
                                        className="form-control mr-2"
                                        style={{ width: 90 }}
                                        value={
                                          isNaN(froms[i])
                                            ? "–"
                                            : froms[i].toFixed(1)
                                        }
                                      />
                                      <span className="mr-2">ถึง</span>
                                      <input
                                        type="number"
                                        step={0.1}
                                        min={0}
                                        className={`form-control mr-2 ${
                                          rowErr?.toKm ? "is-invalid" : ""
                                        }`}
                                        style={{ width: 90 }}
                                        value={row.toKm}
                                        onChange={(e) => {
                                          const rows = bandDraft.rows.map(
                                            (r, idx) =>
                                              idx === i
                                                ? { ...r, toKm: e.target.value }
                                                : r,
                                          );
                                          updateBandDraft({
                                            ...bandDraft,
                                            rows,
                                          });
                                        }}
                                      />
                                      <span className="mr-3">กม.</span>
                                      <span className="mr-2">กม.ละ</span>
                                      <input
                                        type="number"
                                        step={0.01}
                                        min={0}
                                        className={`form-control mr-2 ${
                                          rowErr?.ratePerKm ? "is-invalid" : ""
                                        }`}
                                        style={{ width: 90 }}
                                        value={row.ratePerKm}
                                        onChange={(e) => {
                                          const rows = bandDraft.rows.map(
                                            (r, idx) =>
                                              idx === i
                                                ? {
                                                    ...r,
                                                    ratePerKm: e.target.value,
                                                  }
                                                : r,
                                          );
                                          updateBandDraft({
                                            ...bandDraft,
                                            rows,
                                          });
                                        }}
                                      />
                                      <span className="mr-3">บาท</span>
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => {
                                          const rows = bandDraft.rows.filter(
                                            (_, idx) => idx !== i,
                                          );
                                          updateBandDraft({
                                            ...bandDraft,
                                            rows,
                                          });
                                        }}
                                      >
                                        ลบ
                                      </button>
                                    </div>
                                    {rowErr?.toKm && (
                                      <div className="invalid-feedback d-block">
                                        {rowErr.toKm}
                                      </div>
                                    )}
                                    {rowErr?.ratePerKm && (
                                      <div className="invalid-feedback d-block">
                                        {rowErr.ratePerKm}
                                      </div>
                                    )}
                                    {rateHint && (
                                      <small className="form-text text-muted d-block">
                                        {rateHint}
                                      </small>
                                    )}
                                  </div>
                                </div>
                              );
                            })}

                            {(() => {
                              const froms = deriveBandFroms(bandDraft);
                              const lastFrom = froms[froms.length - 1];
                              const rateHint = legalCapHint(
                                legalCaps?.perKmLegalMin,
                                legalCaps?.perKmLegalMax,
                                "บาท/กม.",
                              );
                              return (
                                <div className="form-group row">
                                  <div className="col-sm-3"></div>
                                  <div className="col-sm-9">
                                    <div className="d-flex align-items-center flex-wrap">
                                      <span className="mr-2">จาก</span>
                                      <input
                                        type="text"
                                        readOnly
                                        disabled
                                        className="form-control mr-2"
                                        style={{ width: 90 }}
                                        value={
                                          isNaN(lastFrom)
                                            ? "–"
                                            : lastFrom.toFixed(1)
                                        }
                                      />
                                      <span className="mr-3">ขึ้นไป</span>
                                      <span className="mr-2">กม.ละ</span>
                                      <input
                                        type="number"
                                        step={0.01}
                                        min={0}
                                        className={`form-control mr-2 ${
                                          bandErrors?.openEndedRate
                                            ? "is-invalid"
                                            : ""
                                        }`}
                                        style={{ width: 90 }}
                                        value={bandDraft.openEndedRate}
                                        onChange={(e) => {
                                          updateBandDraft({
                                            ...bandDraft,
                                            openEndedRate: e.target.value,
                                          });
                                        }}
                                      />
                                      <span>บาท</span>
                                    </div>
                                    {bandErrors?.openEndedRate && (
                                      <div className="invalid-feedback d-block">
                                        {bandErrors.openEndedRate}
                                      </div>
                                    )}
                                    {rateHint && (
                                      <small className="form-text text-muted d-block">
                                        {rateHint}
                                      </small>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}

                            <div className="form-group row">
                              <div className="col-sm-3"></div>
                              <div className="col-sm-9">
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => {
                                    const froms = deriveBandFroms(bandDraft);
                                    const lastFrom = froms[froms.length - 1];
                                    const toKm = isNaN(lastFrom)
                                      ? ""
                                      : (lastFrom + 0.9).toFixed(1);
                                    updateBandDraft({
                                      ...bandDraft,
                                      rows: [
                                        ...bandDraft.rows,
                                        { toKm, ratePerKm: "" },
                                      ],
                                    });
                                  }}
                                >
                                  + เพิ่มช่วง
                                </button>
                              </div>
                            </div>

                            <div className="form-group row">
                              <div className="col-3">
                                <span className="">
                                  ค่าเรียกผ่านระบบ ({currencySymbol})
                                </span>
                              </div>
                              <div className="col-sm-4">
                                <input
                                  type="number"
                                  className="form-control"
                                  min={0}
                                  step={1}
                                  style={{ width: 80 }}
                                  value={carService.callPrice}
                                  onChange={(e) => {
                                    setCarService((prev) => ({
                                      ...prev,
                                      callPrice: +e.target.value,
                                    }));
                                  }}
                                />
                              </div>
                            </div>

                            <div className="form-group row">
                              <div className="col-3">
                                <span className="">
                                  ค่าคอมมิชชั่นตามระยะทาง(%)
                                </span>
                              </div>
                              <div className="col-sm-4">
                                <input
                                  type="number"
                                  className="form-control"
                                  min={0}
                                  step={1}
                                  style={{ width: 80 }}
                                  value={carService.commissionPct}
                                  onChange={(e) => {
                                    setCarService((prev) => ({
                                      ...prev,
                                      commissionPct: +e.target.value,
                                    }));
                                  }}
                                />
                              </div>
                            </div>

                            {/* <div className="form-group row">
                                <div className="col-3"><span className="">ค่าสถาพการจราจร/นาที</span></div>
                                <div className="col-sm-4">
                                  <input
                                    type="number"
                                    className="form-control"
                                    min={0}
                                    step={1}
                                    style={{ width: 80 }}
                                    value={carService.chargePerTraffic}
                                    onChange={(e) => {
                                      setCarService(prev => ({
                                        ...prev,
                                        chargePerTraffic: +e.target.value,
                                      }))
                                    }}
                                  />
                                </div>
                              </div> */}
                          </>
                        )}

                        {carService.serviceType === 0 && (
                          <>
                            {/*<h6 className="mt-3">บริการหลายจุดแวะ</h6>

                            <div className="form-group row">
                              <div className="col-lg-3">เปิดใช้จุดแวะ</div>
                              <div className="col-lg-3">
                                <select
                                  className="form-control"
                                  id="multiStopEnabled"
                                  value={
                                    carService.multiStopEnabled ? "1" : "0"
                                  }
                                  onChange={(e) => {
                                    setCarService((prev) => ({
                                      ...prev,
                                      multiStopEnabled: e.target.value === "1",
                                    }));
                                  }}
                                >
                                  <option value="1">เปิดใช้งาน</option>
                                  <option value="0">ปิดใช้งาน</option>
                                </select>
                              </div>
                            </div>

                            <div className="form-group row">
                              <div className="col-3">
                                <span className="">
                                  ค่าบริการเพิ่มจุด/จุด ({currencySymbol})
                                </span>
                              </div>
                              <div className="col-sm-4">
                                <input
                                  type="number"
                                  className="form-control"
                                  min={0}
                                  step={1}
                                  style={{ width: 80 }}
                                  value={carService.extraStopPrice}
                                  onChange={(e) => {
                                    setCarService((prev) => ({
                                      ...prev,
                                      extraStopPrice: +e.target.value,
                                    }));
                                  }}
                                />
                              </div>
                            </div>

                            <div className="form-group row">
                              <div className="col-3">
                                <span className="">
                                  ค่าคอมมิชชั่นบริการเพิ่มจุด (%)
                                </span>
                              </div>
                              <div className="col-sm-4">
                                <input
                                  type="number"
                                  className="form-control"
                                  min={0}
                                  step={1}
                                  style={{ width: 80 }}
                                  value={carService.extraStopCommissionPct}
                                  onChange={(e) => {
                                    setCarService((prev) => ({
                                      ...prev,
                                      extraStopCommissionPct: +e.target.value,
                                    }));
                                  }}
                                />
                              </div>
                            </div>*/}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header">
                        <h4>
                          ข้อมูลค่าโดยสาร (แสดงในแอปผู้โดยสาร — ไม่ใช้คำนวณราคา)
                        </h4>
                      </div>
                      <div className="card-body">
                        <div className="form-group row">
                          <div className="col-sm-3 col-form-label">
                            ข้อความราคาเริ่มต้น
                          </div>
                          <div className="col-sm-6">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="เริ่มต้น 40 บาท"
                              value={fareInfoDraft.startPriceText}
                              onChange={(e) => {
                                updateFareInfoDraft({
                                  ...fareInfoDraft,
                                  startPriceText: e.target.value,
                                });
                              }}
                            />
                          </div>
                        </div>

                        <h6 className="mt-3">ตารางเรตต่อกม.</h6>

                        {fareInfoDraft.tiers.map((tier, i) => {
                          const tierErr = tierErrors?.[i];
                          return (
                            <div className="form-group row" key={i}>
                              <div className="col-sm-3"></div>
                              <div className="col-sm-9">
                                <div className="d-flex align-items-center flex-wrap">
                                  <span className="mr-2">จาก</span>
                                  <input
                                    type="number"
                                    step={0.1}
                                    min={0}
                                    className={`form-control mr-2 ${
                                      tierErr?.fromKm ? "is-invalid" : ""
                                    }`}
                                    style={{ width: 90 }}
                                    value={tier.fromKm}
                                    onChange={(e) => {
                                      const tiers = fareInfoDraft.tiers.map(
                                        (t, idx) =>
                                          idx === i
                                            ? { ...t, fromKm: e.target.value }
                                            : t,
                                      );
                                      updateFareInfoDraft({
                                        ...fareInfoDraft,
                                        tiers,
                                      });
                                    }}
                                  />
                                  <span className="mr-2">ถึง</span>
                                  <input
                                    type="number"
                                    step={0.1}
                                    min={0}
                                    placeholder="ขึ้นไป"
                                    className={`form-control mr-2 ${
                                      tierErr?.toKm ? "is-invalid" : ""
                                    }`}
                                    style={{ width: 90 }}
                                    value={tier.toKm}
                                    onChange={(e) => {
                                      const tiers = fareInfoDraft.tiers.map(
                                        (t, idx) =>
                                          idx === i
                                            ? { ...t, toKm: e.target.value }
                                            : t,
                                      );
                                      updateFareInfoDraft({
                                        ...fareInfoDraft,
                                        tiers,
                                      });
                                    }}
                                  />
                                  <span className="mr-2">กม.ละ</span>
                                  <input
                                    type="number"
                                    step={0.01}
                                    min={0}
                                    className={`form-control mr-2 ${
                                      tierErr?.pricePerKm ? "is-invalid" : ""
                                    }`}
                                    style={{ width: 90 }}
                                    value={tier.pricePerKm}
                                    onChange={(e) => {
                                      const tiers = fareInfoDraft.tiers.map(
                                        (t, idx) =>
                                          idx === i
                                            ? {
                                                ...t,
                                                pricePerKm: e.target.value,
                                              }
                                            : t,
                                      );
                                      updateFareInfoDraft({
                                        ...fareInfoDraft,
                                        tiers,
                                      });
                                    }}
                                  />
                                  <span className="mr-3">บาท</span>
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm mr-1"
                                    disabled={i === 0}
                                    onClick={() => {
                                      const tiers = [...fareInfoDraft.tiers];
                                      const tmp = tiers[i - 1];
                                      tiers[i - 1] = tiers[i];
                                      tiers[i] = tmp;
                                      updateFareInfoDraft({
                                        ...fareInfoDraft,
                                        tiers,
                                      });
                                    }}
                                  >
                                    ↑
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm mr-1"
                                    disabled={
                                      i === fareInfoDraft.tiers.length - 1
                                    }
                                    onClick={() => {
                                      const tiers = [...fareInfoDraft.tiers];
                                      const tmp = tiers[i + 1];
                                      tiers[i + 1] = tiers[i];
                                      tiers[i] = tmp;
                                      updateFareInfoDraft({
                                        ...fareInfoDraft,
                                        tiers,
                                      });
                                    }}
                                  >
                                    ↓
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => {
                                      const tiers = fareInfoDraft.tiers.filter(
                                        (_, idx) => idx !== i,
                                      );
                                      updateFareInfoDraft({
                                        ...fareInfoDraft,
                                        tiers,
                                      });
                                    }}
                                  >
                                    ลบ
                                  </button>
                                </div>
                                {tierErr?.fromKm && (
                                  <div className="invalid-feedback d-block">
                                    {tierErr.fromKm}
                                  </div>
                                )}
                                {tierErr?.toKm && (
                                  <div className="invalid-feedback d-block">
                                    {tierErr.toKm}
                                  </div>
                                )}
                                {tierErr?.pricePerKm && (
                                  <div className="invalid-feedback d-block">
                                    {tierErr.pricePerKm}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        <div className="form-group row">
                          <div className="col-sm-3"></div>
                          <div className="col-sm-9">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => {
                                updateFareInfoDraft({
                                  ...fareInfoDraft,
                                  tiers: [
                                    ...fareInfoDraft.tiers,
                                    { fromKm: "", toKm: "", pricePerKm: "" },
                                  ],
                                });
                              }}
                            >
                              + เพิ่มแถว
                            </button>
                          </div>
                        </div>

                        <div className="form-group row">
                          <div className="col-sm-3 col-form-label">
                            ข้อความค่ารถติด
                          </div>
                          <div className="col-sm-6">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="3 บาท/นาที"
                              value={fareInfoDraft.trafficRateText}
                              onChange={(e) => {
                                updateFareInfoDraft({
                                  ...fareInfoDraft,
                                  trafficRateText: e.target.value,
                                });
                              }}
                            />
                          </div>
                        </div>

                        <h6 className="mt-3">เงื่อนไข</h6>

                        {fareInfoDraft.conditions.map((cond, i) => (
                          <div className="form-group row" key={i}>
                            <div className="col-sm-3"></div>
                            <div className="col-sm-9">
                              <div className="d-flex align-items-center flex-wrap">
                                <input
                                  type="text"
                                  className="form-control mr-2"
                                  style={{ maxWidth: 400 }}
                                  value={cond}
                                  onChange={(e) => {
                                    const conditions =
                                      fareInfoDraft.conditions.map((c, idx) =>
                                        idx === i ? e.target.value : c,
                                      );
                                    updateFareInfoDraft({
                                      ...fareInfoDraft,
                                      conditions,
                                    });
                                  }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => {
                                    const conditions =
                                      fareInfoDraft.conditions.filter(
                                        (_, idx) => idx !== i,
                                      );
                                    updateFareInfoDraft({
                                      ...fareInfoDraft,
                                      conditions,
                                    });
                                  }}
                                >
                                  ลบ
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}

                        <div className="form-group row">
                          <div className="col-sm-3"></div>
                          <div className="col-sm-9">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => {
                                updateFareInfoDraft({
                                  ...fareInfoDraft,
                                  conditions: [...fareInfoDraft.conditions, ""],
                                });
                              }}
                            >
                              + เพิ่มเงื่อนไข
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row mt-4">
                  <button
                    type="button"
                    className="btn btn-outline-primary mr-2 ml-auto"
                    onClick={() => {
                      navigate(`${process.env.PUBLIC_URL}/car-service`);
                    }}
                  >
                    ยกเลิก
                  </button>
                  <button type="submit" className="btn btn-primary mr-3">
                    บันทึก
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CarServiceForm;
